import styles from "./sprints.module.css";
import { useState, useContext } from "react";
import { useParams } from "react-router-dom";
import {
  Typography,
  Flex,
  Button,
  App,
  Space,
  Spin,
  Divider,
  Pagination,
  Modal,
  Tooltip,
} from "antd";

import PlusIcon from "@/icons/PlusIcon";
import FilterIcon from "@/icons/FilterIcon";
import CloseIcon from "@/icons/CloseIcon";

import Breadcrumbs from "@/components/ui/Breadcrumbs";
import SearchFrom3 from "@/components/inputs/SearchFrom3";
import CustomSelect from "@/components/inputs/CustomSelect";
import CustomTreeSelect from "@/components/inputs/CustomTreeSelect";
import SprintsTable from "@/components/tables/SprintsTable";

import SprintModal from "@/components/modals/SprintModal";
import FiltersModal from "@/components/modals/FiltersModal";

import { UserContext } from "@/context/UserContext";
import MainContext from "@/layouts/MainLayout/context";

import { FiltersModalContent } from "./FiltersModalContent";

import { useFilters } from "./hooks/useFilters";
import { useTableControls } from "./hooks/useTableControls";
import { useManagedUnits } from "./hooks/useManagedUnits";

import { useTableAutoHeight } from "@/hooks/useTableAutoHeight";

import { getList, deleteItem } from "@/api/sprints";

import routes from "@/routes";

import {
  getInitialTableParams,
  getLocalStorageKey,
  FILTER_NAMES,
} from "@/constants/sprints";

import { ACCESS_LEVELS } from "@/constants/tasks";

const { Title } = Typography;

const Sprints = ({ isAdmins = false }) => {
  const { unit_uuid } = useParams();

  const { message } = App.useApp();
  const { user } = useContext(UserContext);
  const { config } = useContext(MainContext);

  const sprintNames = config?.entities?.sprint?.name || {
    singular: "Спринт",
    plural: "Спринты",
  };

  // Данные
  const [data, setData] = useState([]);
  const [dataPending, setDataPending] = useState(true);

  const initialTableParams = getInitialTableParams(unit_uuid);
  const [tableParams, setTableParams] = useState(initialTableParams);

  const [authorSelectOpen, setAuthorSelectOpen] = useState(false);

  // Окно создания/редактирования
  const [isModalShow, setIsModalShow] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);

  // Автоматическая высота таблицы
  const { tableContainerRef, tableHeight, headerHeight } = useTableAutoHeight([
    data,
    tableParams.pagination.pageSize,
  ]);

  // Запрос на сервер для получения данных
  const fetchData = async (tParams) => {
    setDataPending(true);

    const currentParams = tParams || tableParams;

    const sortArray = tParams ? tParams.sort : tableParams.sort;

    const sortString = sortArray
      .map(({ columnKey, order }) => {
        return order === "descend" ? `-${columnKey}` : columnKey;
      })
      .join(",");

    const params = {
      page: currentParams.pagination.current,
      perpage: currentParams.pagination.pageSize,
      sort: sortString,
      ...currentParams.filters,
      type_filter: currentParams.filters.access_level,
    };

    // Дерево работает с объектами, поэтому для запроса нужно преобразовать их в массив uuids
    if (params.units_uuids?.length) {
      params.units_uuids = params.units_uuids.map(({ value }) => value);
    }

    try {
      const { sprints, total, page, perPage } = await getList(params, isAdmins);
      setData(sprints);

      const updatedTableParams = {
        ...currentParams,
        pagination: {
          ...currentParams.pagination,
          total,
          current: page,
          pageSize: perPage,
        },
      };
      setParams(updatedTableParams);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setDataPending(false);
    }
  };

  // Устанавливаем параметры таблицы и сохраняем их в localStorage
  const setParams = (params) => {
    setTableParams(params);
    const toSave = { ...params, filters: { ...params.filters } };

    localStorage.setItem(
      getLocalStorageKey(user.user_uuid, unit_uuid, isAdmins),
      JSON.stringify(toSave),
    );
  };

  // Хук для получения рабочих групп, в которых пользователь может создать спринт
  const { managedUnits, isCanAddSprint } = useManagedUnits(isAdmins, unit_uuid);

  // Хуки для работы с опциями и фильтрами
  const {
    statuses,
    authors,
    unitsTree,
    unitData,
    units,
    tempValueFilters,
    optionsPending,
    isAnyFilterApplied,
    isFiltersModalShow,
    handleFilterChange,
    openFiltersModal,
    clearFilters,
    applyFilters,
    setIsFiltersModalShow,
    handleTempFilterChange,
  } = useFilters({
    unit_uuid,
    userUuid: user.user_uuid,
    initialTableParams,
    tableParams,
    setParams,
    fetchData,
    isAdmins,
  });

  // Хук для управления таблицей
  const { handlePaginationChange, handleTableChange } = useTableControls({
    tableParams,
    setParams,
    fetchData,
  });

  // Обработчик добавления. Открывает модальное окно
  const handleAddNew = () => {
    if (tableParams.filters.unit_uuid) {
      setCurrentItem({
        unit_uuid: tableParams.filters.unit_uuid,
      });
    } else {
      setCurrentItem(null);
    }
    setIsModalShow(true);
  };

  // Обработчик сохранения. Отправляет запрос на сервер и обновляет данные
  const handleSaveItem = () => {
    setIsModalShow(false);
    fetchData();
  };

  // Обработчик редактирования. Открывает модальное окно
  const handleEditItem = (item) => {
    setCurrentItem(item);
    setIsModalShow(true);
  };

  // Обработчик удаления
  const handleDeleteItem = (item) => {
    Modal.confirm({
      title: `Вы уверены, что хотите удалить ${item.name}?`,
      content: (
        <span>
          {sprintNames.singular} будет полностью удален из&nbsp;системы.
          Это&nbsp;действие нельзя будет отменить. Прикрепленные задачи
          останутся в&nbsp;системе.
        </span>
      ),
      onOk() {
        handleConfirmDelete(item);
      },
      okText: "Удалить",
      cancelText: "Отмена",
    });
  };

  // Действительно удаляем спринт после подтверждения
  const handleConfirmDelete = async (item) => {
    setDataPending(true);
    try {
      await deleteItem({ uuid: item.uuid });
      await fetchData();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
      setDataPending(false);
    }
  };

  return (
    <>
      {optionsPending ? (
        <div className="spin">
          <Spin size="large" />
        </div>
      ) : (
        <>
          <Flex vertical gap={25} className="h-100">
            {/* Хлебные крошки и заголовок */}
            <Flex vertical gap={10}>
              <Breadcrumbs
                items={
                  isAdmins
                    ? [
                        { title: "Административная панель" },

                        {
                          title: sprintNames.plural,
                        },
                      ]
                    : [
                        {
                          title: "Главная",
                          href: routes.dashboards,
                        },
                        unitData?.name
                          ? {
                              title: unitData.name,
                            }
                          : {},
                        {
                          title: sprintNames.plural,
                        },
                      ]
                }
              />

              <Flex align="center" justify="space-between" gap={20}>
                <Title level={2} className="no-margin">
                  {sprintNames.plural}
                </Title>

                <Button
                  type="primary"
                  icon={<PlusIcon />}
                  onClick={handleAddNew}
                  disabled={!isCanAddSprint}
                />
              </Flex>
            </Flex>

            {/* Поиск и фильтры */}
            <Flex gap={8} align="center">
              <SearchFrom3
                placeholder="Поиск"
                defaultValue={tableParams.filters.search}
                onSearch={(search) =>
                  handleFilterChange(FILTER_NAMES.SEARCH, search)
                }
                style={{
                  width: 300,
                }}
              />

              <Flex gap={8} align="center" className={styles.mobileFilters}>
                <Button
                  type="primary"
                  icon={<FilterIcon />}
                  onClick={openFiltersModal}
                ></Button>

                {isAnyFilterApplied && (
                  <Tooltip title="Очистить все фильтры" placement="bottom">
                    <Button
                      color="default"
                      variant="link"
                      shape="circle"
                      size="small"
                      icon={<CloseIcon />}
                      onClick={clearFilters}
                      style={{ background: "#BBBBBB", color: "#fff" }}
                    ></Button>
                  </Tooltip>
                )}
              </Flex>

              <Flex gap={8} align="center" className={styles.desktopFilters}>
                {isAdmins ? (
                  <CustomSelect
                    placeholder="Рабочая группа"
                    options={units}
                    fieldNames={{
                      label: "name",
                      value: "uuid",
                      key: "uuid",
                    }}
                    style={{
                      width: 200,
                    }}
                    showSearch
                    optionFilterProp="name"
                    allowClear
                    popupMatchSelectWidth={false}
                    value={tableParams.filters.unit_uuid}
                    onChange={(unit) =>
                      handleFilterChange(FILTER_NAMES.UNIT_UUID, unit)
                    }
                  />
                ) : (
                  <Flex gap={8} align="center">
                    <CustomTreeSelect
                      placeholder="Рабочая группа"
                      data={unitsTree}
                      value={tableParams.filters.units_uuids}
                      onChange={(units_uuids) => {
                        handleFilterChange(
                          FILTER_NAMES.UNITS_UUIDS,
                          units_uuids,
                        );
                      }}
                      style={{
                        width: 200,
                      }}
                    />
                    <CustomSelect
                      placeholder="Тип"
                      options={ACCESS_LEVELS}
                      style={{
                        width: 135,
                      }}
                      popupMatchSelectWidth={false}
                      value={tableParams.filters.access_level}
                      onChange={(val) =>
                        handleFilterChange(FILTER_NAMES.ACCESS_LEVEL, val)
                      }
                      optionRender={(option) => (
                        <Flex gap={5} align="center">
                          {option?.data?.icon}
                          <span>{option.label}</span>
                        </Flex>
                      )}
                      labelRender={(option) => {
                        const item = ACCESS_LEVELS.find(
                          (item) => item.value === option.value,
                        );

                        return (
                          <Flex gap={5} align="center">
                            {item?.icon}
                            <span>{item.label}</span>
                          </Flex>
                        );
                      }}
                    />
                  </Flex>
                )}

                <CustomSelect
                  open={authorSelectOpen}
                  onOpenChange={setAuthorSelectOpen}
                  placeholder="Автор"
                  value={tableParams.filters.author_uuid}
                  options={authors}
                  showSearch
                  allowClear
                  fieldNames={{ value: "uuid" }}
                  optionFilterProp="label"
                  style={{ width: 200 }}
                  placement="topRight"
                  popupMatchSelectWidth={false}
                  onChange={(val) =>
                    handleFilterChange(FILTER_NAMES.AUTHOR_UUID, val)
                  }
                  popupRender={(menu) => (
                    <>
                      {menu}
                      {authors
                        .map((u) => u.uuid)
                        .includes(user.employee_uuid) && (
                        <>
                          <Divider style={{ margin: "8px 0" }} />
                          <Space style={{ padding: "0 8px 4px" }}>
                            <Typography.Link
                              onClick={() => {
                                handleFilterChange(
                                  FILTER_NAMES.AUTHOR_UUID,
                                  user.employee_uuid,
                                );
                                setAuthorSelectOpen(false);
                              }}
                            >
                              Я автор
                            </Typography.Link>
                          </Space>
                        </>
                      )}
                    </>
                  )}
                />
              </Flex>
            </Flex>

            {/* Контент */}
            <Flex vertical gap={20} style={{ flex: 1, minHeight: 0 }}>
              <div ref={tableContainerRef} style={{ flex: 1, minHeight: 0 }}>
                <SprintsTable
                  data={data}
                  loading={dataPending}
                  pagination={false}
                  sortedInfo={tableParams.sort}
                  unit={unit_uuid}
                  isAdmins={isAdmins}
                  resize
                  scroll={{ x: "100%", y: tableHeight - headerHeight }}
                  onEditItem={handleEditItem}
                  onDeleteItem={handleDeleteItem}
                  onChange={handleTableChange}
                />
              </div>

              <div style={{ minHeight: 32 }}>
                <Pagination
                  {...tableParams.pagination}
                  onChange={handlePaginationChange}
                />
              </div>
            </Flex>
          </Flex>

          {isModalShow && (
            <SprintModal
              data={currentItem}
              visible={isModalShow}
              onSave={handleSaveItem}
              onCancel={() => setIsModalShow(false)}
              unit_uuid={unit_uuid}
              units={isAdmins ? units : managedUnits}
              statuses={statuses}
            />
          )}

          {isFiltersModalShow && (
            <FiltersModal
              visible={isFiltersModalShow}
              onSave={applyFilters}
              onCancel={() => {
                setIsFiltersModalShow(false);
              }}
              onClear={() => {
                setIsFiltersModalShow(false);
                clearFilters();
              }}
            >
              <FiltersModalContent
                isAdmins={isAdmins}
                tempValueFilters={tempValueFilters}
                units={units}
                unitsTree={unitsTree}
                authors={authors}
                user={user}
                handleTempFilterChange={handleTempFilterChange}
              />
            </FiltersModal>
          )}
        </>
      )}
    </>
  );
};

export default Sprints;
